/**
 * Seletor com ícone (conta, cartão, categoria): um botão que mostra a escolha atual e abre uma
 * lista com logo/ícone por opção — o <select> nativo não desenha imagem. Fecha com Esc, clique
 * fora ou ao escolher. Com mais de 7 opções aparece uma busca.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface PickerOption {
  id: string;
  label: string;
  hint?: string;
  icon?: ReactNode;
  group?: string;
}

interface Props {
  value: string;
  options: PickerOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  /** opção "nenhum" (valor '') no topo da lista */
  emptyLabel?: string;
  disabled?: boolean;
  testId?: string;
}

function Caret() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-mute">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function Picker({ value, options, onChange, placeholder = 'Escolha…', emptyLabel, disabled, testId }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((o) => o.id === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q)) : options;
  const groups = [...new Set(shown.map((o) => o.group ?? ''))];
  const pick = (id: string) => { onChange(id); setOpen(false); setQuery(''); };
  const optionTestId = testId ? `${testId}-option` : undefined;

  return (
    <div ref={root} className="relative mt-1">
      <button type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-controls={listId}
        onClick={() => setOpen((o) => !o)} data-testid={testId} data-value={value}
        className="flex w-full items-center gap-2.5 rounded-xl border border-rule bg-paper px-3 py-2 text-left text-[15px] text-ink outline-none focus:border-teal disabled:opacity-60">
        {selected?.icon}
        <span className={`min-w-0 flex-1 truncate ${selected || (value === '' && emptyLabel) ? '' : 'text-mute'}`}>
          {selected ? selected.label : value === '' && emptyLabel ? emptyLabel : placeholder}
        </span>
        {selected?.hint && <span className="shrink-0 text-[12px] text-mute">{selected.hint}</span>}
        <Caret />
      </button>
      {open && (
        <div id={listId} role="listbox" className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-rule bg-surface p-1 shadow-lg">
          {options.length > 7 && (
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar…" aria-label="Buscar"
              className="mb-1 w-full rounded-lg border border-rule bg-paper px-2.5 py-1.5 text-[14px] text-ink outline-none focus:border-teal" />
          )}
          {emptyLabel !== undefined && !q && (
            <button type="button" role="option" aria-selected={value === ''} onClick={() => pick('')} data-testid={optionTestId} data-value=""
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] hover:bg-paper ${value === '' ? 'bg-teal-pale text-teal-dark' : 'text-mute'}`}>
              {emptyLabel}
            </button>
          )}
          {groups.map((g) => (
            <div key={g}>
              {g && <div className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-mute">{g}</div>}
              {shown.filter((o) => (o.group ?? '') === g).map((o) => (
                <button key={o.id} type="button" role="option" aria-selected={o.id === value} onClick={() => pick(o.id)} data-testid={optionTestId} data-value={o.id}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] hover:bg-paper ${o.id === value ? 'bg-teal-pale text-teal-dark' : 'text-ink'}`}>
                  {o.icon}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.hint && <span className="shrink-0 text-[12px] text-mute">{o.hint}</span>}
                </button>
              ))}
            </div>
          ))}
          {shown.length === 0 && <p className="px-2.5 py-3 text-[13px] text-mute">Nada com esse nome.</p>}
        </div>
      )}
    </div>
  );
}
